// Material design
import { useNavigate } from "react-router-dom";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Icon,
  useTheme,
  Avatar,
  Tooltip,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Autocomplete,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";

// SVG png import
import Loginlogo from "../../assets/images/tharagreen-logo-white.svg";

// Multi-lingual support
import { t } from "i18next";
import i18n from "../../locales/locales";

// API Manager
import { getApi } from "../../services/apiManager/ApiManager";

//image-logo
import Weather from "../../assets/images/Weather.png";

// Header component
const Header: React.FC = () => {
  const languages = [
    { code: "th", label: "ไทย (Thai)" },
    { code: "en", label: "English" },
  ];
  const [loggedInUser, setLoggedInUser] = useState<string>("");
  // Language selection state
  const [language, setLanguage] = useState<string>(() => {
    return localStorage.getItem("lng") || "th";
  });

  useEffect(() => {
    const fetchUser = async () => {
      const response = await getApi("/v1/user/current");
      if (response && response.data && response.data.name) {
        setLoggedInUser(response.data.name);
      }
    };
    fetchUser();
  }, []);

  // Navigation instance
  const navigate = useNavigate();

  // Logout
  const handleLogout = () => {
    const preservedKey = "lng";
    const preservedValue = localStorage.getItem(preservedKey);

    localStorage.clear();

    if (preservedValue !== null) {
      localStorage.setItem(preservedKey, preservedValue);
    }
    navigate("/authenticate");
  };

  const theme = useTheme();

  // Handle language change
  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    const selectedLang = event.target.value as string;
    setLanguage(selectedLang);
    localStorage.setItem("lng", selectedLang);
    i18n.changeLanguage(selectedLang);
    window.location.reload();
  };

  return (
    <>
      {/* <h1>Agriculture Admin</h1> */}
      <Box
        component="div"
        sx={{
          boxShadow: 3,
          width: 230,
          height: 90,
          marginTop: "20px",
          marginLeft: "15px",
          borderRadius: "25px 25px 0 0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "farm.main",
          border: "solid 1px #019b24",
          borderBottom: "none",
          background: theme.palette.agrigradient.siderbarheadgrd,
        }}
      >
        <Box
          component="img"
          src={Loginlogo}
          alt="Thara Green"
          sx={{
            display: "flex",
            width: 55,
            height: 75,
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: `calc(100% - 270px)`,
          marginTop: "10px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            component="img"
            src={Weather}
            alt="Sun Icon"
            sx={{
              width: 30,
              height: 20,
              marginRight: 1,
              alignSelf: "flex-start",
              marginTop: "8px",
            }}
          />
          <Box
            sx={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "farm.contrastText",
                fontWeight: "bold",
                fontSize: 16,
                marginBottom: "2px",
              }}
            >
              {t("header.goodDay")}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "farm.contrastText",
                fontSize: 16,
              }}
            >
              {t("header.grow")}
            </Typography>
          </Box>
        </Box>

        <Box>
          <nav>
            <List
              sx={{
                display: "flex",
                flexDirection: "row",
                marginRight: 3,
                alignItems: "center",
              }}
            >
              <ListItem sx={{ minWidth: "auto", paddingRight: 1 }}>
                {/* Language Selector */}
                <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
                  <Autocomplete
                    size="small"
                    disableClearable
                    options={languages}
                    getOptionLabel={(option) => option.label}
                    value={languages.find((lang) => lang.code === language)}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        handleLanguageChange({
                          target: {
                            name: "language",
                            value: newValue.code,
                          },
                        } as SelectChangeEvent<string>);
                      }
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.code === value?.code
                    }
                    sx={{
                      minWidth: 120,
                      cursor: "pointer",
                      "& .MuiInputBase-root": {
                        color: "white",
                        borderRadius: "12px",
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                        cursor: "pointer",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "white",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "white",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "white",
                      },
                      "& .MuiSvgIcon-root, & .MuiAutocomplete-popupIndicator": {
                        color: "white",
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("header.language")}
                        sx={{
                          "& label": {
                            color: "white",
                            fontWeight: 500,
                            fontSize: "0.85rem",
                            cursor: "pointer",
                          },
                          "& label.Mui-focused": {
                            color: "white",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "white" },
                            "&:hover fieldset": { borderColor: "white" },
                            "&.Mui-focused fieldset": {
                              borderColor: "white",
                            },
                            borderRadius: "12px",
                          },
                          "& input": {
                            color: "white",
                            padding: "8px 10px",
                            fontSize: "0.85rem",
                          },
                        }}
                      />
                    )}
                  />
                </Box>
              </ListItem>
              {/* Avatar with initials */}
              <ListItem sx={{ minWidth: "auto", paddingRight: 1 }}>
                <Avatar
                  sx={{
                    bgcolor: "secondary.main",
                    width: 32,
                    height: 32,
                    fontSize: 14,
                  }}
                >
                  {loggedInUser
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()}
                </Avatar>
              </ListItem>
              <ListItem
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  paddingLeft: 1,
                }}
              >
                <ListItemText
                  primary={loggedInUser}
                  sx={{
                    color: "farm.contrastText",
                    fontSize: 14,
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                  }}
                />
              </ListItem>
              {/* <ListItem>
              <Icon
                className="icon-bell2"
                sx={{
                  marginLeft: 1,
                  marginRight: 1,
                  color: "#FFFFFF",
                  cursor: "pointer",
                }}
              />
            </ListItem> */}
              <ListItem onClick={handleLogout} sx={{ cursor: "pointer" }}>
                <Tooltip title={t("header.logout")} arrow>
                  <Icon
                    className="icon-logout"
                    sx={{
                      marginLeft: 1,
                      marginRight: 1,
                      color: "#FFFFFF",
                    }}
                  />
                </Tooltip>
              </ListItem>
            </List>
          </nav>
        </Box>
      </Box>
    </>
  );
};

export default Header;
